import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../shared/constants/env';
import { useUser } from './UserContext';

export type Currency = 'INR' | 'USD' | 'EUR' | 'GBP' | 'AUD' | 'CAD' | 'SGD';
export type Language = 'en' | 'es' | 'fr' | 'de' | 'ja' | 'zh' | 'hi' | 'ta' | 'te' | 'kn' | 'ml';

interface PreferencesContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  language: Language;
  setLanguage: (language: Language) => Promise<void>;
  currencySymbols: Record<Currency, string>;
  currencyNames: Record<Currency, string>;
  languageNames: Record<Language, string>;
}

const currencySymbols: Record<Currency, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
};

const currencyNames: Record<Currency, string> = {
  INR: 'Indian Rupee',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  SGD: 'Singapore Dollar',
};

const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  ja: 'Japanese',
  zh: 'Chinese',
  hi: 'Hindi',
  ta: 'Tamil',
  te: 'Telugu',
  kn: 'Kannada',
  ml: 'Malayalam',
};

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const { user, setUser } = useUser();
  const [currency, setCurrencyState] = useState<Currency>('INR');
  const [language, setLanguageState] = useState<Language>('en');
  const [isLoaded, setIsLoaded] = useState(false);
  const apiRoot = API_BASE_URL.replace(/\/graphql\/?$/, '');

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('userCurrency') as Currency | null;
    const savedLanguage = localStorage.getItem('userLanguage') as Language | null;

    if (savedCurrency) setCurrencyState(savedCurrency);
    if (user?.preferredLanguage) {
      setLanguageState(user.preferredLanguage as Language);
    } else if (savedLanguage) {
      setLanguageState(savedLanguage);
    }
    setIsLoaded(true);
  }, [user?.preferredLanguage]);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('userCurrency', newCurrency);
  };

  const setLanguage = async (newLanguage: Language) => {
    const previousLanguage = language;
    setLanguageState(newLanguage);
    localStorage.setItem('userLanguage', newLanguage);
    // Update HTML lang attribute
    document.documentElement.lang = newLanguage;

    if (!user?.email) {
      return;
    }

    try {
      const response = await fetch(`${apiRoot}/api/v1/users/preferred-language`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          preferred_language: newLanguage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to persist language preference');
      }

      setUser({
        ...user,
        preferredLanguage: newLanguage,
      });
    } catch (err) {
      console.error('Unable to persist language preference', err);
      setLanguageState(previousLanguage);
      localStorage.setItem('userLanguage', previousLanguage);
      document.documentElement.lang = previousLanguage;
    }
  };

  if (!isLoaded) {
    return <>{children}</>;
  }

  return (
    <PreferencesContext.Provider
      value={{
        currency,
        setCurrency,
        language,
        setLanguage,
        currencySymbols,
        currencyNames,
        languageNames,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
